import React, { useEffect, useRef, useState } from "react";

/**
 * AnimatedActionIcon — shows a static icon normally, and plays a Lottie
 * animation once each time `trigger` changes (e.g. on click). Falls back to the
 * static icon if lottie-web fails to load, so the button always works.
 *
 * Props:
 *  - animationData: the imported Lottie JSON
 *  - icon: the static React node (e.g. <Heart/>) shown when idle
 *  - trigger: any changing value (increment a counter on click to replay)
 *  - size: px
 */
export default function AnimatedActionIcon({ animationData, icon, trigger, size = 22, className = "" }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const first = useRef(true);

  useEffect(() => {
    // Don't play on initial mount — only when the trigger actually changes.
    if (first.current) { first.current = false; return; }
    let anim;
    let cancelled = false;
    setPlaying(true);
    import("lottie-web")
      .then((mod) => {
        if (cancelled || !ref.current) return;
        const lottie = mod.default || mod;
        ref.current.innerHTML = "";
        anim = lottie.loadAnimation({
          container: ref.current, renderer: "svg", loop: false, autoplay: true, animationData,
        });
        anim.addEventListener("complete", () => setPlaying(false));
      })
      .catch(() => setPlaying(false));
    return () => { cancelled = true; if (anim) anim.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger]);

  return (
    <span className={`inline-flex items-center justify-center ${className}`} style={{ width: size, height: size, position: "relative" }}>
      <span ref={ref} style={{ width: size, height: size, display: playing ? "block" : "none" }} />
      {!playing && icon}
    </span>
  );
}
